import { DEFAULT_SHIFT } from '@xeetr/types/defaults'
import { ViewShift } from '@xeetr/types/models'
import { copyShift } from '@xeetr/types/utils'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import AddObjectButton from '../../components/settings/common/AddObjectButton'
import ShiftForm from '../../components/settings/shifts/ShiftForm'
import ShiftHeader from '../../components/settings/shifts/ShiftHeader'
import ShiftsFooter from '../../components/settings/shifts/ShiftsFooter'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import Loading from '@/components/loading/Loading'
import { useConfirmDialog } from '@/hooks/common/useConfirmDialog'
import { useShifts } from '@/hooks/useShifts'
import { useShiftsMutation } from '@/hooks/useShiftsMutation'
import { DirtyFormsMap, FormControlHandle } from '@/types/ShiftForm'

const Shifts = () => {
  const { shifts, isLoading } = useShifts()
  const { createShift, updateShift, deleteShift } = useShiftsMutation()
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [activeFormControl, setActiveFormControl] = useState<FormControlHandle | null>(null)
  const [dirtyForms, setDirtyForms] = useState<DirtyFormsMap>(new Map())
  const confirmDialog = useConfirmDialog()

  const handleCreate = () => {
    const newShift: ViewShift = {
      ...DEFAULT_SHIFT,
      id: uuidv4(),
    }
    createShift(newShift)
    // Expand the new shift after creation
    setTimeout(() => setExpandedItems([newShift.id]), 100)
  }

  const handleCopy = (shift: ViewShift) => {
    const copiedShift: ViewShift = {
      ...copyShift(shift),
      id: uuidv4(),
    }
    createShift(copiedShift)
  }

  const handleDelete = (id: string) => {
    confirmDialog.confirm(
      {
        title: 'Löschen bestätigen!',
        description: 'Möchten Sie die Schicht wirklich löschen?',
        confirmText: 'Löschen',
        cancelText: 'Abbrechen',
        variant: 'destructive',
      },
      () => {
        deleteShift(id)
        // Remove from expanded if was open
        setExpandedItems((prev) => prev.filter((item) => item !== id))
      }
    )
  }

  const handleSwap = (fromIndex: number, toIndex: number) => {
    // Update order for both shifts
    const shiftsCopy = [...shifts]
    const fromShift = shiftsCopy[fromIndex]
    const toShift = shiftsCopy[toIndex]

    updateShift({
      id: fromShift.id,
      shift: { order: toShift.order },
    })
    updateShift({
      id: toShift.id,
      shift: { order: fromShift.order },
    })
  }

  /**
   * Handle accordion value change with dirty form warning
   * If user tries to close a dirty form, show confirmation dialog
   */
  const handleAccordionChange = (newExpandedItems: string[]) => {
    const closingItems = expandedItems.filter((item) => !newExpandedItems.includes(item))

    // Check if any closing item has dirty form
    const dirtyClosingItem = closingItems.find((item) => dirtyForms.get(item))

    if (dirtyClosingItem) {
      // Show warning dialog
      confirmDialog.confirm(
        {
          title: 'Nicht gespeicherte Änderungen',
          description:
            'Sie haben nicht gespeicherte Änderungen. Möchten Sie diese verwerfen und fortfahren?',
          confirmText: 'Verwerfen',
          cancelText: 'Abbrechen',
          variant: 'destructive',
        },
        () => {
          // User confirmed, allow close and discard changes
          setExpandedItems(newExpandedItems)
          setDirtyForms((prev) => {
            const next = new Map(prev)
            next.delete(dirtyClosingItem)
            return next
          })
        }
      )
    } else {
      // No dirty forms, allow close
      setExpandedItems(newExpandedItems)
    }
  }

  /**
   * Register/unregister form control from child ShiftForm
   * Updates dirty state tracking
   */
  const handleRegisterForm = (handle: FormControlHandle | null) => {
    setActiveFormControl(handle)

    // Update dirty state map
    if (handle) {
      setDirtyForms((prev) => {
        const next = new Map(prev)
        next.set(handle.shiftId, handle.isDirty)
        return next
      })
    }
  }

  return (
    <div className="flex flex-col">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Schichten</h2>
        <p className="text-muted-foreground text-sm">
          Verwalten Sie die Schichten Ihres Restaurants, um Arbeitszeiten und Verfügbarkeiten zu
          organisieren.
        </p>
      </div>
      <div className="flex justify-end">
        <AddObjectButton append={handleCreate} label="Neue Schicht" />
      </div>
      <Loading loading={isLoading} label="Schichten werden geladen...">
        <Accordion
          type="multiple"
          className="mt-4"
          value={expandedItems}
          onValueChange={handleAccordionChange}
        >
          {shifts.map((shift, shiftIndex) => (
            <AccordionItem key={shift.id} value={shift.id}>
              <AccordionTrigger>
                <ShiftHeader
                  shift={shift}
                  shiftIndex={shiftIndex}
                  shifts={shifts}
                  swapShift={handleSwap}
                />
              </AccordionTrigger>
              <AccordionContent>
                <ShiftForm
                  shift={shift}
                  isExpanded={expandedItems.includes(shift.id)}
                  onRegisterForm={handleRegisterForm}
                  onCopy={() => handleCopy(shift)}
                  onDelete={() => handleDelete(shift.id)}
                  onUpdate={(updatedShift) =>
                    updateShift({ id: shift.id, shift: updatedShift })
                  }
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Loading>
      <ConfirmDialog
        open={confirmDialog.isOpen}
        onOpenChange={confirmDialog.setIsOpen}
        title={confirmDialog.options.title}
        description={confirmDialog.options.description}
        confirmText={confirmDialog.options.confirmText}
        cancelText={confirmDialog.options.cancelText}
        variant={confirmDialog.options.variant}
        onConfirm={confirmDialog.handleConfirm}
      />
      <ShiftsFooter formControl={activeFormControl} />
    </div>
  )
}

export default Shifts
